/* SearchSelect — a searchable single-select (combobox), drop-in replacement for a
   native <select className='input'>. Options accept plain strings or {value, label}.
   Built on MUI Autocomplete so it filters as you type; styled to match the supply
   `.input` look (compact, indigo focus ring). */
import React from 'react'
import PropTypes from 'prop-types'
import {Autocomplete, TextField, CircularProgress, InputAdornment} from '@mui/material'

export default function SearchSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  invalid = false,
  loading = false,
  sx = {},
}) {
  const opts = options.map((o) => (o && typeof o === 'object' ? o : {value: o, label: String(o)}))
  const selected = opts.find((o) => String(o.value) === String(value)) || null

  return (
    <Autocomplete
      options={opts}
      value={selected}
      onChange={(_, v) => onChange(v ? v.value : '')}
      getOptionLabel={(o) => (o && o.label != null ? String(o.label) : '')}
      isOptionEqualToValue={(o, v) => String(o.value) === String(v && v.value)}
      disabled={disabled}
      size='small'
      fullWidth
      autoHighlight
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={!!invalid}
          InputProps={{
            ...params.InputProps,
            // show a spinner up front (even when the menu is closed) while loading
            startAdornment: loading ? (
              <InputAdornment position='start' sx={{ml: 0.5}}>
                <CircularProgress size={15} />
              </InputAdornment>
            ) : (
              params.InputProps.startAdornment
            ),
          }}
        />
      )}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '6px',
          fontSize: 13,
          background: '#fff',
          py: '1px',
        },
        '& .MuiOutlinedInput-root .MuiAutocomplete-input': {fontSize: 13, py: '5.5px'},
        '& .MuiOutlinedInput-notchedOutline': {borderColor: invalid ? 'var(--error, #DC2626)' : 'var(--border, #E5E7EB)'},
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: 'var(--primary, #6366F1)', borderWidth: 1},
        ...sx,
      }}
      slotProps={{paper: {sx: {fontSize: 13, '& .MuiAutocomplete-option': {fontSize: 13, minHeight: 34}}}}}
    />
  )
}

SearchSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  loading: PropTypes.bool,
  sx: PropTypes.object,
}
